import React, { createContext, useContext, useEffect, useState } from 'react'
import type { User } from 'firebase/auth'
import { getFirebaseAuth, isFirebaseConfigured } from '../config/firebase'
import {
  createGithubProvider,
  createGoogleProvider,
  isAllowedEmailDomain,
  loadFirebaseAuthSdk,
} from './authFirebaseProviders'
import {
  deriveApprovalStatus,
  deriveGlobalAdminStatus,
  parseEmailList,
} from './authApprovalHelpers'
import { buildDevLoginProfile, buildLocalDemoProfile } from './authDemoProfiles'
import { clearAuthStorage, saveUserProfile } from './authStorage'
import { useAuthStateInitialization } from './authStateInitialization'
import type { ApprovalStatus, AuthContextType, UserProfile } from './authTypes'
import {
  DEMO_LOGIN_DISPLAY_NAME,
  DEMO_LOGIN_EMAIL,
  DEV_LOGIN_SECRET,
  isBackendDevLoginEnabled,
  isDemoLoginEnabled,
} from '../config/demoAuth'
import { API_BASE_URL, globalAdminsAPI } from '../services/api'
import { isOnPremEdition } from '../config/edition'

// Firebase가 설정되어 있는지 확인
const isFirebaseAvailable = isFirebaseConfigured

const AuthContext = createContext<AuthContextType | undefined>(undefined)
const AUTH_EXPIRED_EVENT = 'infergraph-auth-expired'

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const useOptionalAuth = () => useContext(AuthContext)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false)
  const [isDemoUser, setIsDemoUser] = useState(false)
  const [approvalStatus, setApprovalStatus] =
    useState<ApprovalStatus>('unknown')
  const [approvalError, setApprovalError] = useState<string | null>(null)
  const isApproved = approvalStatus === 'approved'

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined

    const handleAuthExpired = () => {
      setCurrentUser(null)
      setUserProfile(null)
      setIsGlobalAdmin(false)
      setIsDemoUser(false)
      setApprovalStatus('unknown')
      setApprovalError('로그인이 만료되었습니다. 다시 로그인해주세요.')
    }

    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired)
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired)
    }
  }, [])

  // NOTE (Phase C / TASK_003 — 2026-06-07): The frontend used to merge a
  // hardcoded ``DEFAULT_GLOBAL_ADMIN_EMAILS`` + ``VITE_GLOBAL_ADMIN_EMAILS``
  // env list and flip ``isGlobalAdmin`` purely on email match. That made the
  // SPA render admin UI for any email on the local list even when the backend
  // refused the request. The frontend now treats the backend (signin profile
  // flag + ``/api/global-admins/me``) as the sole source of truth.

  // 승인된 일반 사용자 (admin 외 추가 허용 목록)
  const envApprovedEmails = parseEmailList(
    import.meta.env.VITE_APPROVED_EMAILS as string | undefined
  )

  const deriveIsGlobalAdmin = (
    _email?: string | null,
    profileFlag?: boolean
  ) => {
    return deriveGlobalAdminStatus({ profileFlag })
  }

  // 신규 가입자는 default 승인대기. admin 또는 approved 목록에 있는 이메일만 즉시 접근 가능.
  // 데모 유저 (isDemoUser=true) 는 항상 승인된 것으로 처리.
  const deriveIsApproved = (
    email?: string | null,
    profileFlag?: boolean,
    isDemo?: boolean
  ): boolean => {
    return deriveApprovalStatus({
      email,
      profileFlag,
      isDemo,
      envApprovedEmails,
      isGlobalAdmin: deriveIsGlobalAdmin,
    })
  }

  const setLocalApprovalFallback = (
    email?: string | null,
    profileFlag?: boolean,
    isDemo?: boolean
  ) => {
    setApprovalError(null)
    setApprovalStatus(
      deriveIsApproved(email, profileFlag, isDemo) ? 'approved' : 'unknown'
    )
  }

  const syncSignupApproval = (
    payload: {
      email: string
      display_name?: string | null
      firebase_uid?: string | null
    },
    fallbackApproved: boolean
  ) => {
    // On-prem has no signup-approval concept: an administrator provisions local
    // users directly (Settings > On-prem > 사용자 관리), so a signed-in local
    // user is approved by construction. Calling the SaaS endpoint here is not
    // just redundant — it FAILS: the installer's bootstrap admin is
    // ``admin@infergraph.local`` and ``.local`` is a reserved special-use TLD
    // that email-validator rejects (422), which left an approval error in state
    // and a console error on every page load of a fresh install.
    if (isOnPremEdition) {
      setApprovalError(null)
      setApprovalStatus('approved')
      return
    }
    import('../services/userSignupApi')
      .then(({ userSignupApi }) => userSignupApi.signin(payload))
      .then((signup) => {
        setApprovalError(null)
        setApprovalStatus(signup.status)
      })
      .catch((err) => {
        const message = err?.message || '가입 승인 상태를 확인하지 못했습니다.'
        setApprovalError(message)
        setApprovalStatus(fallbackApproved ? 'approved' : 'error')
      })
  }

  // 사용자 프로필 생성/업데이트
  const createOrUpdateUserProfile = (
    user: User,
    additionalData: Partial<UserProfile> = {}
  ) => {
    // Phase C: ``isGlobalAdmin`` comes from the backend only. ``additionalData``
    // may carry a stale flag (e.g. restored localStorage). We seed the profile
    // with whatever is provided but the authoritative refresh happens in the
    // ``useEffect`` below that calls ``globalAdminsAPI.me()``.
    const computedGlobalAdmin = Boolean(additionalData.isGlobalAdmin)
    const computedApproved = deriveIsApproved(
      user.email,
      additionalData.isGlobalAdmin,
      user.isAnonymous
    )
    setApprovalError(null)
    setApprovalStatus(computedApproved ? 'approved' : 'unknown')

    const defaultProfile: UserProfile = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      role: user.isAnonymous ? 'guest' : 'user',
      isGlobalAdmin: computedGlobalAdmin,
      createdAt: new Date(),
      lastLoginAt: new Date(),
      preferences: {
        theme: 'light',
        language: 'ko',
      },
      ...additionalData,
    }

    setUserProfile(defaultProfile)
    setIsGlobalAdmin(computedGlobalAdmin)

    // 로컬스토리지에 저장
    saveUserProfile(defaultProfile)

    // 백엔드 user_signups 에 가입 기록 + 현재 status 동기화
    if (!user.isAnonymous && user.email) {
      syncSignupApproval(
        {
          email: user.email,
          display_name: user.displayName,
          firebase_uid: user.uid,
        },
        computedApproved
      )
    }
  }

  // localStorage 복원 / 데모 로그인 / SSR 등 createOrUpdateUserProfile 을 거치지 않는 경로 커버.
  React.useEffect(() => {
    const email = currentUser?.email || userProfile?.email
    if (
      isDemoUser ||
      currentUser?.isAnonymous ||
      deriveIsApproved(email, userProfile?.isGlobalAdmin, false)
    ) {
      setLocalApprovalFallback(
        email,
        userProfile?.isGlobalAdmin,
        isDemoUser || currentUser?.isAnonymous
      )
      return
    }
    if (!email) {
      setApprovalError(null)
      setApprovalStatus('unknown')
    }
  }, [
    currentUser?.email,
    currentUser?.isAnonymous,
    userProfile?.email,
    userProfile?.isGlobalAdmin,
    isDemoUser,
  ])

  React.useEffect(() => {
    const email = currentUser?.email || userProfile?.email
    if (!email || isDemoUser || currentUser?.isAnonymous) return
    const fallbackApproved = deriveIsApproved(
      email,
      userProfile?.isGlobalAdmin,
      false
    )
    syncSignupApproval(
      {
        email,
        display_name: currentUser?.displayName || userProfile?.displayName,
        firebase_uid: currentUser?.uid || userProfile?.uid,
      },
      fallbackApproved
    )
  }, [currentUser?.uid, userProfile?.uid, isDemoUser])

  // 이메일/비밀번호 로그인
  const login = async (email: string, password: string) => {
    const auth = await getFirebaseAuth()
    if (!isFirebaseAvailable || !auth) {
      throw new Error(
        'Firebase가 설정되지 않았습니다. Google 로그인을 사용하세요.'
      )
    }
    const { signInWithEmailAndPassword } = await loadFirebaseAuthSdk()
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    )
    createOrUpdateUserProfile(userCredential.user)
  }

  // 회원가입
  const register = async (
    email: string,
    password: string,
    displayName?: string
  ) => {
    const auth = await getFirebaseAuth()
    if (!isFirebaseAvailable || !auth) {
      throw new Error('Firebase가 설정되지 않았습니다.')
    }
    const { createUserWithEmailAndPassword, updateProfile } =
      await loadFirebaseAuthSdk()
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    )

    if (displayName) {
      await updateProfile(userCredential.user, { displayName })
    }

    createOrUpdateUserProfile(userCredential.user, { displayName })
  }

  // 로그아웃
  const logout = async () => {
    // 데모 유저인 경우 또는 Firebase가 없는 경우 signOut 스킵
    const auth = await getFirebaseAuth()
    if (!isDemoUser && isFirebaseAvailable && auth) {
      const { signOut } = await loadFirebaseAuthSdk()
      await signOut(auth)
    }
    setCurrentUser(null)
    setUserProfile(null)
    setIsGlobalAdmin(false)
    setIsDemoUser(false)
    setApprovalStatus('unknown')
    setApprovalError(null)
    clearAuthStorage()
  }

  // 비밀번호 재설정
  const resetPassword = async (email: string) => {
    const auth = await getFirebaseAuth()
    if (!isFirebaseAvailable || !auth) {
      throw new Error('Firebase가 설정되지 않았습니다.')
    }
    const { sendPasswordResetEmail } = await loadFirebaseAuthSdk()
    await sendPasswordResetEmail(auth, email)
  }

  // Google 로그인
  const loginWithGoogle = async () => {
    const auth = await getFirebaseAuth()
    if (!isFirebaseAvailable || !auth) {
      throw new Error(
        'Firebase가 설정되지 않았습니다. Google 로그인을 사용하세요.'
      )
    }
    const { signInWithPopup, signOut, signInWithRedirect } =
      await loadFirebaseAuthSdk()
    const googleProvider = await createGoogleProvider()
    try {
      // 먼저 팝업 방식 시도
      try {
        const userCredential = await signInWithPopup(auth, googleProvider)

        // 도메인 검증
        if (!isAllowedEmailDomain(userCredential.user.email)) {
          await signOut(auth)
          throw new Error(
            `로그인할 수 없는 계정입니다. (현재: ${userCredential.user.email})`
          )
        }

        createOrUpdateUserProfile(userCredential.user)
      } catch (popupError: any) {
        // 도메인 검증 에러는 그대로 전달
        if (popupError.message?.includes('로그인할 수 없는 계정')) {
          throw popupError
        }
        console.warn('팝업 로그인 실패, 리다이렉트 방식으로 전환:', popupError)
        // 팝업이 차단된 경우 리다이렉트 방식 사용
        if (
          popupError.code === 'auth/popup-blocked' ||
          popupError.code === 'auth/popup-closed-by-user'
        ) {
          await signInWithRedirect(auth, googleProvider)
        } else {
          throw popupError
        }
      }
    } catch (error) {
      console.error('Google 로그인 실패:', error)
      throw error
    }
  }

  // GitHub 로그인
  const loginWithGitHub = async () => {
    const auth = await getFirebaseAuth()
    if (!isFirebaseAvailable || !auth) {
      throw new Error(
        'Firebase가 설정되지 않았습니다. Google 로그인을 사용하세요.'
      )
    }
    const { signInWithPopup } = await loadFirebaseAuthSdk()
    const githubProvider = await createGithubProvider()
    const userCredential = await signInWithPopup(auth, githubProvider)
    createOrUpdateUserProfile(userCredential.user)
  }

  // 게스트 로그인
  const loginAsGuest = async () => {
    const auth = await getFirebaseAuth()
    if (!isFirebaseAvailable || !auth) {
      throw new Error(
        'Firebase가 설정되지 않았습니다. Google 로그인을 사용하세요.'
      )
    }
    const { signInAnonymously } = await loadFirebaseAuthSdk()
    const userCredential = await signInAnonymously(auth)
    createOrUpdateUserProfile(userCredential.user, {
      displayName: '게스트 사용자',
      role: 'guest',
    })
  }

  const loginAsDemo = async (
    persona?: import('../config/demoAuth').DevLoginPersona
  ): Promise<void> => {
    if (!isDemoLoginEnabled()) {
      throw new Error(
        '테스트 로그인은 로컬/테스트 환경에서만 사용할 수 있습니다.'
      )
    }

    const personaEmail = persona?.email || DEMO_LOGIN_EMAIL
    const personaDisplayName = persona?.displayName || DEMO_LOGIN_DISPLAY_NAME
    const personaProjectRole = persona?.projectRole

    if (isBackendDevLoginEnabled()) {
      const response = await fetch(`${API_BASE_URL}/api/dev-auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Dev-Login-Secret': DEV_LOGIN_SECRET,
        },
        body: JSON.stringify({
          email: personaEmail,
          displayName: personaDisplayName,
          ...(personaProjectRole ? { projectRole: personaProjectRole } : {}),
        }),
      })

      if (!response.ok) {
        const text = await response.text()
        throw new Error(`DEV 로그인 실패 (${response.status}): ${text}`)
      }

      const data = await response.json()
      const devProfile = buildDevLoginProfile(data)

      setCurrentUser(null)
      setUserProfile(devProfile)
      setIsGlobalAdmin(Boolean(devProfile.isGlobalAdmin))
      setIsDemoUser(true)
      setApprovalStatus('approved')
      setApprovalError(null)
      saveUserProfile(devProfile)
      localStorage.setItem('isDemoUser', 'true')
      localStorage.setItem('auth_token', data.accessToken)
      localStorage.setItem('auth_user', JSON.stringify(devProfile))
      return
    }

    const demoProfile = buildLocalDemoProfile(persona)

    setCurrentUser(null)
    setUserProfile(demoProfile)
    setIsGlobalAdmin(false)
    setIsDemoUser(true)
    setApprovalStatus('approved')
    setApprovalError(null)
    saveUserProfile(demoProfile)
    localStorage.setItem('isDemoUser', 'true')
    localStorage.removeItem('auth_token')
    localStorage.removeItem('auth_user')
    localStorage.removeItem('refresh_token')
  }

  // 백엔드 직접 로그인 (Firebase 없이 이메일/사용자명 + 패스워드)
  const loginWithCredentials = async (usernameOrEmail: string, password: string): Promise<void> => {
    const formData = new URLSearchParams()
    formData.append('username', usernameOrEmail)
    formData.append('password', password)

    const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: usernameOrEmail, password }),
    })

    if (!response.ok) {
      throw new Error('credentials_failed')
    }

    const data = await response.json()
    const accessToken: string = data.access_token

    // 토큰 저장
    localStorage.setItem('auth_token', accessToken)

    // 응답에서 직접 프로필 구성 (email, display_name 포함)
    let profile: UserProfile
    if (data.email) {
      profile = {
        uid: data.email,
        email: data.email,
        displayName: data.display_name || usernameOrEmail,
        photoURL: null,
        role: 'user',
        isGlobalAdmin: false,
        createdAt: new Date(),
        lastLoginAt: new Date(),
        preferences: { theme: 'light', language: 'ko' },
      }
    } else {
      // fallback 최소 프로필
      profile = {
        uid: usernameOrEmail,
        email: usernameOrEmail.includes('@') ? usernameOrEmail : `${usernameOrEmail}@infergraph.local`,
        displayName: usernameOrEmail,
        photoURL: null,
        role: 'user',
        isGlobalAdmin: false,
        createdAt: new Date(),
        lastLoginAt: new Date(),
        preferences: { theme: 'light', language: 'ko' },
      }
    }

    setCurrentUser(null)
    setUserProfile(profile)
    setIsGlobalAdmin(Boolean(profile.isGlobalAdmin))
    setIsDemoUser(false)
    setApprovalStatus('approved')
    setApprovalError(null)
    saveUserProfile(profile)
    localStorage.setItem('auth_user', JSON.stringify(profile))
  }

  // 사용자 프로필 업데이트
  const updateUserProfile = async (data: Partial<UserProfile>) => {
    if (!userProfile) return

    try {
      // Firebase Auth 프로필 업데이트 (displayName만)
      if (currentUser && data.displayName !== undefined) {
        const { updateProfile } = await loadFirebaseAuthSdk()
        await updateProfile(currentUser, {
          displayName: data.displayName,
        })
      }

      // 로컬 프로필 상태 업데이트 — Phase C: ``isGlobalAdmin`` is owned by the
      // backend, so we preserve whatever flag the caller / current profile
      // already carries instead of re-deriving from email.
      const updatedProfile = {
        ...userProfile,
        ...data,
        isGlobalAdmin: Boolean(data.isGlobalAdmin ?? userProfile.isGlobalAdmin),
      }
      setUserProfile(updatedProfile)
      setIsGlobalAdmin(updatedProfile.isGlobalAdmin || false)

      // 로컬스토리지에 저장
      saveUserProfile(updatedProfile)
    } catch (error) {
      console.error('프로필 업데이트 실패:', error)
      throw error
    }
  }

  // 선택된 프로젝트 업데이트
  const updateSelectedProject = async (projectId: string) => {
    if (!userProfile) return

    try {
      const updatedPreferences = {
        ...userProfile.preferences,
        selectedProjectId: projectId,
      }

      const updatedProfile = {
        ...userProfile,
        preferences: updatedPreferences,
      }

      setUserProfile(updatedProfile)
      saveUserProfile(updatedProfile)
    } catch (error) {
      console.error('선택된 프로젝트 업데이트 실패:', error)
      throw error
    }
  }

  useAuthStateInitialization({
    isDemoUser,
    setCurrentUser,
    setUserProfile,
    setIsGlobalAdmin,
    setIsDemoUser,
    setApprovalStatus,
    setApprovalError,
    setLoading,
    deriveIsGlobalAdmin,
    createOrUpdateUserProfile,
  })

  // 현재 사용자 글로벌 관리자 여부 동기화 — Phase C: backend is the SoT.
  // We seed from the profile flag (set by signin / dev-login restore) and then
  // overwrite with whatever ``/api/global-admins/me`` reports. If the backend
  // says false, we set false — no email-list fallback.
  useEffect(() => {
    const email = userProfile?.email
    const profileFlag = userProfile?.isGlobalAdmin

    if (!email) {
      setIsGlobalAdmin(false)
      return
    }

    // Seed state from whatever the profile carries (may be stale on first paint).
    setIsGlobalAdmin(Boolean(profileFlag))

    if (isDemoUser) {
      return
    }

    let cancelled = false
    const fetchAdminStatus = async () => {
      try {
        const res = await globalAdminsAPI.me()
        if (cancelled) return
        const backendFlag = Boolean(res.data?.isGlobalAdmin)
        setIsGlobalAdmin(backendFlag)
        // Propagate the authoritative flag back into the cached profile so
        // downstream reads (e.g. ``userProfile.isGlobalAdmin``) agree with
        // the SPA-level ``isGlobalAdmin`` state.
        setUserProfile((current) =>
          current && current.isGlobalAdmin !== backendFlag
            ? { ...current, isGlobalAdmin: backendFlag }
            : current
        )
      } catch (e) {
        if (cancelled) return
        // On lookup failure, fall back to whatever the profile already carries
        // (typically ``false`` for non-admins). We do NOT re-introduce the
        // hardcoded email list.
        setIsGlobalAdmin(Boolean(profileFlag))
      }
    }

    fetchAdminStatus()
    return () => {
      cancelled = true
    }
  }, [userProfile?.email, userProfile?.isGlobalAdmin, isDemoUser])

  const value: AuthContextType = {
    currentUser,
    userProfile,
    isGlobalAdmin,
    isApproved,
    approvalStatus,
    approvalError,
    loading,
    isDemoUser,
    login,
    register,
    logout,
    resetPassword,
    loginWithGoogle,
    loginWithGitHub,
    loginAsGuest,
    loginAsDemo,
    loginWithCredentials,
    updateUserProfile,
    updateSelectedProject,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export default AuthContext
